import { Box, Typography } from '@mui/material';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { spaces } from '../../../../assets/styles/themes/tokens';
import { searchSettings } from '../../../../constants';
import useQueryModification from '../../../../routes/SearchPage/useQueryModification';
import type { SearchFilters } from '../../../../types/search';
import getTextTruncationSx from '../../../../utils/getTextTruncationSx';
import useRouter from '../../../../utils/useRouter';
import useScreenSize from '../../../../utils/useScreenSize';
import SortByFilterNewUI from '../../../SearchFilters/SortByFilterNewUI';

export type SearchResultsTopbarNewUIProps = {
	total: number;
	searchTerm?: string;
	requestFilters: SearchFilters;
	isLoaded: boolean;
	withSortByFilter?: boolean;
};

const SearchResultsTopbarNewUI: React.FC<SearchResultsTopbarNewUIProps> = ({
	total,
	searchTerm,
	requestFilters,
	isLoaded = false,
	withSortByFilter = true,
}) => {
	const { t } = useTranslation();
	const { isXsScreen } = useScreenSize();
	const searchTermText = searchTerm ? `“${searchTerm}”` : t('all results');
	const totalText = total > 0 ? `${total} ` : null;
	const { router, match } = useRouter();
	const { updateQueryParams } = useQueryModification<SearchFilters>(router, match);

	if (!isLoaded) return null;

	return (
		<Box
			data-testid="search-results-topbar"
			sx={{
				display: 'inline-flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				width: '100%',
				marginY: spaces.md,
				color: 'text.primary',
				...(isXsScreen
					? {
							flexDirection: 'column',
						}
					: {}),
			}}
		>
			<Box
				data-testid="search-results-topbar__total"
				sx={{
					...(isXsScreen ? { marginRight: 'auto' } : {}),
				}}
			>
				<Typography
					data-testid="search-results-header-new-ui"
					variant="subtitle1"
					sx={{
						maxWidth: isXsScreen ? '92vw' : '58vw', // We need that line to crop the long search term
						color: 'text.primary',
						whiteSpace: 'nowrap',
						...getTextTruncationSx(),
					}}
				>
					<Trans
						i18nKey={'<totalText> search results for "<searchTermText>"'}
						values={{ totalText, searchTermText }}
					>
						<Box
							data-testid="search-results-header-new-ui__query"
							component="span"
							sx={{ fontWeight: '600' }}
						>
							{searchTermText}
						</Box>
					</Trans>
				</Typography>
			</Box>
			{withSortByFilter && !isXsScreen && (
				<Box
					data-testid="sort-by-new-ui-filter"
					sx={{
						...(isXsScreen ? { marginLeft: 'auto' } : {}),
					}}
				>
					<SortByFilterNewUI
						currentValue={requestFilters.sortBy}
						updateFilter={(sortBy) => updateQueryParams({ sortBy })}
						clearFilter={() => updateQueryParams({ sortBy: searchSettings.defaultSortBy })}
					/>
				</Box>
			)}
		</Box>
	);
};

export default SearchResultsTopbarNewUI;
